import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HumanReviewQueueSection } from "@/features/patient/human-review/components/HumanReviewQueueView";
import { ReviewItemView } from "@/features/patient/medications/components/ReviewItemView";
import {
  getReviewProcessingApi,
  listReviewItemsApi,
  submitReviewResponseApi,
  type ReviewItem,
  type ReviewProcessing,
} from "@/portals/patient/api/reviewItemsApi";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { useProfileFromQuery } from "@/portals/patient/hooks/useProfileFromQuery";
import { useReviewItemsCountStore } from "@/stores/reviewItemsCountStore";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useLiveRefreshFallback } from "@/shared/hooks/useLiveRefreshFallback";
import { AppModal } from "@/shared/components/dialog";
import { PageSpinner } from "@/shared/components/spinner";
import { toastError, toastSuccess } from "@/shared/lib/toast";

export const SKIPPED_MESSAGE = "Sent to your clinician without an answer.";

/** How often the processing banner re-checks while an upload is being worked on. */
export const PROCESSING_POLL_MS = 3_000;
/** How long the "your new records are checked" summary stays once the work finishes. */
export const FINISHED_SUMMARY_MS = 5_000;

/**
 * The last loaded queue and progress per profile, for this browser session:
 * coming back to the page shows them at once (then refreshes quietly) instead
 * of a full-page spinner while the list reloads. Cleared on logout.
 */
const pageCache = new Map<string, { items?: ReviewItem[]; processing?: ReviewProcessing }>();
usePatientAuthStore.subscribe((state) => {
  if (!state.accessToken) pageCache.clear();
});
const cacheKey = (profileId?: string) => profileId ?? "self";
/** For tests: forget everything cached. */
export function resetHumanReviewQueueCache() {
  pageCache.clear();
}
const isBusy = (p?: ReviewProcessing | null) => Boolean(p && p.state !== "idle");

/**
 * /patient/human-review-queue — the patient's medication review items. Review
 * opens the same review modal as before (answer + comment → clinician); Skip
 * sends the item on without an answer. Both land in the clinician / shared
 * queue and the admin's view.
 */
export function HumanReviewQueuePage() {
  useProfileFromQuery();
  // ?item= — a notification (e.g. the clinician's question) opens that item selected.
  const [searchParams] = useSearchParams();
  const focusItemId = searchParams.get("item");
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;
  const { refresh: refreshRecords, refreshToken } = useParsedRecords();
  const refreshCount = useReviewItemsCountStore((s) => s.refresh);

  const key = cacheKey(actingAsProfileId);
  const [items, setItems] = useState<ReviewItem[] | null>(() => pageCache.get(key)?.items ?? null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [openReviewId, setOpenReviewId] = useState<string | null>(null);
  const [skippingId, setSkippingId] = useState<string | null>(null);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    // Another profile: show what we already have for it straight away.
    const cached = pageCache.get(key)?.items;
    if (cached) setItems(cached);
    listReviewItemsApi("all", actingAsProfileId).then(({ data, error: loadError }) => {
      if (cancelled) return;
      if (data) {
        setItems(data.items);
        pageCache.set(key, { ...pageCache.get(key), items: data.items });
        setError(null);
      } else if (!items) {
        setError(loadError?.message ?? "Couldn't load your review queue. Please try again later.");
      }
    });
    return () => {
      cancelled = true;
    };
    // Refetched with the records (uploads, answers) and on every explicit reload.
  }, [actingAsProfileId, refreshToken, tick]);

  // Safety net so the queue never goes stale (same as the other live pages).
  useLiveRefreshFallback(reload);

  const afterChange = useCallback(() => {
    reload();
    refreshRecords();
    void refreshCount(actingAsProfileId);
  }, [reload, refreshRecords, refreshCount, actingAsProfileId]);

  // New records still being read / matched / compared: show progress,
  // re-check every few seconds while busy, and when it finishes reload the
  // queue and show every step done (with the outcome) for a moment, so the
  // card never just vanishes. Seeded from the cache, so leaving mid-way and
  // coming back after it finished still shows that summary.
  const [processing, setProcessing] = useState<ReviewProcessing | null>(() => pageCache.get(key)?.processing ?? null);
  const [pollTick, setPollTick] = useState(0);
  const [justFinished, setJustFinished] = useState(false);
  const wasBusy = useRef(isBusy(pageCache.get(key)?.processing));
  const finishedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(finishedTimer.current), []);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    getReviewProcessingApi(actingAsProfileId).then(({ data }) => {
      if (cancelled) return;
      if (!data) {
        // A failed check never freezes the card on its last step: try again.
        if (wasBusy.current) timer = setTimeout(() => setPollTick((t) => t + 1), PROCESSING_POLL_MS * 2);
        return;
      }
      setProcessing(data);
      pageCache.set(key, { ...pageCache.get(key), processing: data });
      const busy = isBusy(data);
      if (busy) {
        timer = setTimeout(() => setPollTick((t) => t + 1), PROCESSING_POLL_MS);
        setJustFinished(false);
      } else if (wasBusy.current) {
        afterChange();
        setJustFinished(true);
        clearTimeout(finishedTimer.current);
        finishedTimer.current = setTimeout(() => setJustFinished(false), FINISHED_SUMMARY_MS);
      }
      wasBusy.current = busy;
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Also re-checked with every queue reload (uploads, answers, live updates).
  }, [actingAsProfileId, key, refreshToken, tick, pollTick, afterChange]);

  const skip = async (itemId: string) => {
    setSkippingId(itemId);
    const { error: skipError } = await submitReviewResponseApi(itemId, { answer: "SKIPPED" }, actingAsProfileId);
    setSkippingId(null);
    if (skipError && skipError.code !== "ITEM_NOT_AWAITING_INPUT") {
      toastError(skipError.message);
    } else if (!skipError) {
      toastSuccess(SKIPPED_MESSAGE);
    }
    afterChange();
  };

  if (error && !items) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center py-24 text-center">
        <p className="text-sm font-semibold text-[#F36F8F]">{error}</p>
      </div>
    );
  }
  if (!items) return <PageSpinner className="bg-[#F7F7F7]" />;

  return (
    <>
      <HumanReviewQueueSection items={items} focusItemId={focusItemId} processing={processing} justFinished={justFinished} onReview={setOpenReviewId} onSkip={(id) => void skip(id)} skippingId={skippingId} />
      <AppModal
        open={openReviewId !== null}
        onOpenChange={(open) => { if (!open) setOpenReviewId(null); }}
        title={<span className="text-[#0A2947]">Medication review</span>}
        description={<span className="text-[#4B5563]">Tell us about this medicine so your clinician can confirm it.</span>}
        width={860}
        footer={null}
      >
        {openReviewId && (
          <ReviewItemView
            key={`${actingAsProfileId ?? "self"}:${openReviewId}`}
            variant="modal"
            itemId={openReviewId}
            actingAsProfileId={actingAsProfileId}
            onBack={() => setOpenReviewId(null)}
            onItemChanged={afterChange}
          />
        )}
      </AppModal>
    </>
  );
}
